import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {useSelector, connect} from 'react-redux';
import useDisplayConfig from 'src/hooks/useDisplayConfig';
import {
  useMessagingInboxDrawer,
  useTRMEnabled,
  useTrmAppShellEnabled,
} from 'src/hooks/product-flags';
import flow from 'lodash/flow';
import classify from 'src/utils/classify';
import {hasPrivilege} from 'src/utils/accounts';
import {agentHasPhone} from 'src/selectors/chat';
import {setAIPanelOpenEvent} from 'src/action-creators/sense-ai-panel';
import {
  USE_MESSAGING,
  USE_JD_GENERATOR,
  USE_PRESCREENING_GENERATOR,
  USE_RESUME_GENERATOR,
} from 'src/action-creators/roles';
import {selectUrlSuffix} from 'src/selectors/sense-ai-panel';
import {addQueryParamsToUrl} from 'src/utils/index';
import {selectBroadcastRecipientLimit} from 'src/selectors/broadcasts';
import {selectTotalUnreadThreadCount} from 'src/selectors/messages';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import BulkMessageWrarning from 'src/components/navigation/bulk-message-warning';
import {
  Panel,
  PanelBody,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {
  Modal,
  ModalBody,
} from '@spaced-out/ui-design-system/lib/components/Modal';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {ResumeParsing} from 'src/components/app-shell/resume-parsing';
import CrmNotifications from 'src/components/app-shell/crm-notifications';
import css from './common-top-nav-buttons.css';


type CommonTopNavButtonsProps = {
  authed?: boolean;
  setIsAiPanelOpen?: (value: boolean) => void;
  showAIPanel?: boolean;
  hasSenseAIPermission?: boolean;
  canViewChat?: boolean;
};

type IframePayload = {
  phoneNumbers?: string[];
  type?: null;
};

type MessagingDetails = {
  type?: 'MULTIPLE_RECIPIENT' | 'CLICKED_NUMBER';
  payload?: IframePayload;
};

const mapStateToProps = (state: any) => {
  return {
    authed: state.accounts.authedUserId !== null,
    canViewChat: agentHasPhone(state) && hasPrivilege(state, USE_MESSAGING),
    showAIPanel: state.senseAIPanel.isAiPanelOpen,
    hasSenseAIPermission:
      hasPrivilege(state, USE_JD_GENERATOR) ||
      hasPrivilege(state, USE_PRESCREENING_GENERATOR) ||
      hasPrivilege(state, USE_RESUME_GENERATOR),
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  setIsAiPanelOpen(value: any) {
    dispatch(setAIPanelOpenEvent(value));
  },
});

const CommonTopNavButtonsComponent = (props: CommonTopNavButtonsProps) => {
  const {
    authed,
    setIsAiPanelOpen,
    showAIPanel,
    canViewChat,
    hasSenseAIPermission,
  } = props;

  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [showMessagingFrame, setShowMessagingFrame] = React.useState(false);
  const [messagingDetails, setMessagingDetails] =
    React.useState<MessagingDetails | null>(null);
  const [showUserLimitModal, setShowUserLimitModal] = React.useState(false);
  const [iframeReady, setIframeReady] = React.useState(false);

  const senseMessagingRef = React.useRef<HTMLIFrameElement | null>(null);
  const urlSuffix = useSelector(selectUrlSuffix);
  const userLimit = useSelector(selectBroadcastRecipientLimit);
  const totalUnreadCount: number = useSelector(selectTotalUnreadThreadCount);
  const showMessagingInboxDrawer = useMessagingInboxDrawer();
  const landingPageView = useDisplayConfig('landing_page_view');
  const trmEnabled = useTRMEnabled();
  const isTrmAppShellEnabled = useTrmAppShellEnabled();
  const showMessagingDrawerIcon =
    canViewChat && showMessagingInboxDrawer && (landingPageView || trmEnabled);

  const showTrmParsingIcon = trmEnabled && isTrmAppShellEnabled;

  const handleOpenMessagingPanel = (data: unknown) => {
    // @ts-expect-error - TS2339 - Property 'detail' does not exist on type 'unknown'.
    const {detail} = data;
    if (
      detail?.type === 'MULTIPLE_RECIPIENT' &&
      detail?.payload?.phoneNumbers?.length > userLimit
    ) {
      setShowUserLimitModal(true);
    } else {
      setShowMessagingFrame(true);
      setMessagingDetails(detail);
      setIframeReady(false);
    }
  };

  // Handle iframe ready signal from the messaging app
  // @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type.
  const handleIframeReadyMessage = React.useCallback((event) => {
    if (event.origin === window.origin && event.data?.type === 'IFRAME_READY') {
      setIframeReady(true);
    }
  }, []);

  // React 19-safe: send postMessage only when iframe is ready and messagingDetails is set
  React.useEffect(() => {
    if (iframeReady && messagingDetails) {
      const iframe = senseMessagingRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(messagingDetails, window.origin);
      }
    }
  }, [iframeReady, messagingDetails]);

  let senseAiPanelUrl = window.location.origin;
  if (urlSuffix) {
    senseAiPanelUrl += `/sense-ai-panel${urlSuffix}`;
  } else {
    senseAiPanelUrl += `/sense-ai-panel`;
  }

  senseAiPanelUrl = addQueryParamsToUrl(senseAiPanelUrl, {
    context: 'sense-ai',
  });

  const isTopNavButtons =
    showTrmParsingIcon || showMessagingDrawerIcon || hasSenseAIPermission;

  React.useEffect(() => {
    window.addEventListener('openMessagingPanel', handleOpenMessagingPanel);
    window.addEventListener('message', handleIframeReadyMessage);
    return () => {
      window.removeEventListener(
        'openMessagingPanel',
        handleOpenMessagingPanel,
      );
      window.removeEventListener('message', handleIframeReadyMessage);
    };
  }, [handleIframeReadyMessage]);

  if (!isTopNavButtons || !authed) {
    return null;
  }

  return (
    <div className={css.buttonsContainer}>
      {showTrmParsingIcon && <ResumeParsing />}
      {showTrmParsingIcon && <CrmNotifications />}
      {showMessagingDrawerIcon && (
        <>
          <div className={css.badgeContainer}>
            <Tooltip
              title={t('Inbox', 'Inbox')}
              delayMotionDuration="slowest"
              placement="bottom"
            >
              <Button
                iconLeftName="message"
                onClick={() => setShowMessagingFrame((prev) => !prev)}
                size="small"
                type="ghost"
                iconLeftType="solid"
                classNames={{wrapper: css.buttonWithBadge}}
                ariaLabel={t('Inbox', 'Inbox')}
              />
            </Tooltip>

            {totalUnreadCount > 0 ? (
              <Badge
                fill="blueDark"
                text={`${totalUnreadCount}`}
                classNames={{
                  wrapper: classify(css.messageCountBadge, {
                    [css.multipleDigits]: totalUnreadCount > 9,
                  }),
                }}
              />
            ) : null}
          </div>
          {showMessagingFrame ? (
            <Panel
              anchor="right"
              initialFocus={2}
              onClose={() => {
                setShowMessagingFrame(false);
                setMessagingDetails(null);
                setIframeReady(false);
              }}
              size="medium"
              tapOutsideToClose
              isOpen={showMessagingFrame}
            >
              <PanelBody className={css.panelPaddingNull}>
                <iframe
                  width={'100%'}
                  src={`${window.location.origin}/messages?context=trm-iframe`}
                  title="messaging"
                  style={{border: 0, height: '100vh'}}
                  ref={senseMessagingRef}
                />
              </PanelBody>
            </Panel>
          ) : null}
        </>
      )}
      {hasSenseAIPermission && (
        <div className={css.senseAIPanelBtn}>
          <Tooltip
            title={t('AI_Copilot', 'AI Copilot')}
            delayMotionDuration="slowest"
            placement="bottom-end"
          >
            <Button
              iconLeftName="sparkles"
              onClick={() => setIsAiPanelOpen && setIsAiPanelOpen(true)}
              size="small"
              type="ghost"
              iconLeftType="solid"
              ariaLabel={t('AI_Copilot', 'AI Copilot')}
            />
          </Tooltip>
          {showAIPanel && (
            <Panel
              anchor="right"
              initialFocus={2}
              size="large"
              tapOutsideToClose
              onClose={() => setIsAiPanelOpen && setIsAiPanelOpen(false)}
              isOpen={showAIPanel}
            >
              <iframe
                width={'100%'}
                src={senseAiPanelUrl}
                title="AI Copilot"
                style={{border: 0, height: '100vh'}}
              />
            </Panel>
          )}
        </div>
      )}
      <Modal
        isOpen={showUserLimitModal}
        onClose={() => setShowUserLimitModal(false)}
      >
        <ModalBody>
          <BulkMessageWrarning
            userLimit={userLimit}
            onClose={() => setShowUserLimitModal(false)}
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

export const CommonTopNavButtons = flow(
  connect(mapStateToProps, mapDispatchToProps),
)(
  CommonTopNavButtonsComponent,
) as React.ComponentType<CommonTopNavButtonsProps>;
